import { AxiosRequestConfig } from "axios";
import { BarChartType } from "components/barChart";
import { LineChartType } from "components/lineChart";
import { PieChartType } from "components/pieChart";
import {
  EnvironmentEnum,
  OptionType,
  ReportEnum,
  SubChannelTypeEnum,
} from "models";

// Values
export const activeStatus = 1;

export const allStatus = 0;

export const aytyErrorLabel = "#AYTY_EXCEPTION:";

export const customerIsOut = -15;

export const customerEnded = -16;

export const dddList = [
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35,
  37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 64, 63,
  65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
];

export const developmentIdRole = 7;

export const documentationPath =
  "https://ligocloud.atlassian.net/wiki/spaces/DA/pages/2620489729/Ligo+CRM+Cloud";

export const emptyValue = "empty";

const { development } = EnvironmentEnum;

const environment = process.env.REACT_APP_ENVIRONMENT || development;

export const environmentIsDevelopment = environment === development;

export const i18nLanguage = "i18nextLng";

export const inactiveStatus = -1;

export const itemsPerPaginations: number[] = [4, 8, 12, 16, 20];

export const keyboardKeys = {
  esc: "Escape",
  enter: "Enter",
  up: "ArrowUp",
  down: "ArrowDown",
};

export const languages = ["pt", "en", "es"];

export const messageSubChannel = [
  SubChannelTypeEnum.Chat,
  SubChannelTypeEnum.ChatExternal,
  SubChannelTypeEnum.WhatsApp,
];

export const metricsComplementsPath =
  "https://portalcode7.liq.com.br/AYTY/AppATMACONTAX/AytyTechSuiteFrontEndWeb_inst4/download/AYTY_Installer_MetricsComplement.exe";

export const modalRoot = document.getElementById("modal");

export const newItem = -1;

export const popupSize = { x: 600, y: 400 };

export const textEditorTheme = {
  heading: {
    h1: "editor-heading-h1",
    h2: "editor-heading-h2",
  },
  image: "editor-image",
  list: {
    listitem: "editor-listitem",
    nested: {
      listitem: "editor-nested-listitem",
    },
    ol: "editor-list-ol",
    ul: "editor-list-ul",
  },
  ltr: "ltr",
  paragraph: "editor-paragraph",
  rtl: "rtl",
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    strikethrough: "editor-text-strikethrough",
    underline: "editor-text-underline",
  },
};

export const unauthorizedCode = -999;

export const unauthorizedToken = 419;

export const voiceSubChannel = [
  SubChannelTypeEnum.Inbound,
  SubChannelTypeEnum.Predictive,
  SubChannelTypeEnum.Preview,
  SubChannelTypeEnum.Offline,
];

// Objects
export const attendanceErrors = {
  alreadyInAttendance: -2,
  noVoiceAnswering: -3,
  invalidQueue: -4,
  unassociatedUser: -5,
  voiceClientExists: -6,
};

export const dashboradChartBarOptions: Omit<BarChartType, "data"> = {
  colors: ({ id, data }) => String((data as any)[`${id}Color`]),
  $width: "100%",
  $height: "24rem",
  padding: 0.3,
  animate: false,
  enableGridX: false,
  enableGridY: false,
  margin: { bottom: 58 },
  labelTextColor: "#FFFFFF",
  valueScale: { type: "linear" },
  legends: [
    {
      dataFrom: "indexes",
      anchor: "bottom-left",
      direction: "row",
      translateY: 58,
      itemsSpacing: 2,
      itemWidth: 100,
      itemHeight: 20,
      itemDirection: "left-to-right",
      symbolSize: 10,
      symbolShape: "circle",
    },
  ],
};

export const dashboradChartBarGroupedOptions: Omit<BarChartType, "data"> = {
  colors: ({ id, data }) => String((data as any)[`${id}Color`]),
  $width: "100%",
  $height: "24rem",
  padding: 0.3,
  innerPadding: 2,
  gridYValues: 5,
  animate: false,
  enableGridX: false,
  labelTextColor: "#FFFFFF",
  groupMode: "grouped",
  margin: { bottom: 58, left: 40, top: 10 },
  axisLeft: { tickValues: 5, legendOffset: -40 },
  legends: [
    {
      dataFrom: "keys",
      anchor: "bottom",
      direction: "row",
      translateY: 58,
      itemsSpacing: 2,
      itemWidth: 200,
      itemHeight: 20,
      itemDirection: "left-to-right",
      symbolSize: 10,
      symbolShape: "circle",
    },
  ],
};

export const dashboradChartLineOptions: Omit<LineChartType, "data"> = {
  $width: "100%",
  $height: "24rem",
  curve: "monotoneX",
  margin: { left: 10, top: 20, right: 10, bottom: 58 },
  yScale: {
    type: "linear",
  },
  pointSize: 10,
  pointColor: { from: "series.color", modifiers: [] },
  pointBorderColor: { from: "seriesColor" },
  enablePointLabel: true,
  pointLabelYOffset: -12,
  enableTouchCrosshair: true,
  enableSlices: "x",
  enableArea: true,
  useMesh: true,
  legends: [
    {
      anchor: "bottom-left",
      direction: "row",
      translateY: 58,
      itemsSpacing: 2,
      itemWidth: 120,
      itemHeight: 20,
      itemDirection: "left-to-right",
      symbolSize: 10,
      symbolShape: "circle",
    },
  ],
};

export const dashboardChartPieOptions: Omit<PieChartType, "data"> = {
  $width: "100%",
  $height: "24rem",
  animate: false,
  colors: { scheme: "category10" },
  padAngle: 0.4,
  innerRadius: 0.5,
  enableArcLabels: false,
  enableArcLinkLabels: true,
  arcLinkLabelsSkipAngle: 10,
  arcLinkLabelsThickness: 2,
  arcLabelsRadiusOffset: 1.3,
  arcLinkLabelsColor: { from: "color" },
  margin: { left: 50, top: 25, right: 50, bottom: 25 },
  arcLabelsTextColor: "var(--text)",
};

export const formDataConfig: AxiosRequestConfig = {
  headers: { "Content-Type": "multipart/form-data" },
  transformRequest: (formData) => formData,
};

export const JSONConfig: AxiosRequestConfig = {
  headers: { "Content-Type": "application/json" },
};

// Options
export const coreReportOpts: OptionType<ReportEnum>[] = [
  { label: "labels.overview", value: ReportEnum.overview },
  { label: "labels.licenseAnalytic", value: ReportEnum.licenseAnalytic },
  {
    label: "labels.licenseConsolidated",
    value: ReportEnum.licenseConsolidated,
  },
  { label: "labels.usersInRealTime", value: ReportEnum.usersInRealTime },
];

export const defaultReportOpts: OptionType<ReportEnum>[] = [
  { label: "labels.interactions", value: ReportEnum.interaction },
  {
    label: "labels.interactionAndRecording",
    value: ReportEnum.interactionAndRecording,
  },
  { label: "labels.interactionMap", value: ReportEnum.interactionMap },
  {
    label: "labels.operationsPerformance",
    value: ReportEnum.operationsPerformance,
  },
  {
    label: "labels.operationsPerformanceByOrder",
    value: ReportEnum.operationsPerformanceByOrder,
  },
  {
    label: "labels.detailedSales",
    value: ReportEnum.detailedSales,
  },
  {
    label: "labels.ticketAnalytic",
    value: ReportEnum.ticketAnalytic,
  },
  {
    label: "labels.ticketConsolidated",
    value: ReportEnum.ticketConsolidated,
  },
  { label: "labels.calls", value: ReportEnum.call },
  { label: "labels.orders", value: ReportEnum.order },
  { label: "labels.usersInRealTime", value: ReportEnum.usersInRealTime },
];

export const defaultStatusOpts: OptionType<
  typeof allStatus | typeof activeStatus | typeof inactiveStatus
>[] = [
  { value: allStatus, label: "labels.all" },
  { value: activeStatus, label: "labels.active" },
  { value: inactiveStatus, label: "labels.inactive" },
];

export const dsReportOpts: OptionType<ReportEnum>[] = [];

export * from "./datetime";
export * from "./file";
export * from "./locale";
export * from "./regex";
export * from "./storage";
